(function () {
  'use strict';

  function calculateBackoff(attempt, baseDelay = 1000, maxDelay = 30000) {
    const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);

    const jitter = Math.random() * 0.1 * delay;
    return Math.floor(delay + jitter);
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function createRetryConfig(options = {}) {
    return {
      maxRetries: options.maxRetries || 3,
      retryDelay: options.retryDelay || 1000,
      maxDelay: options.maxDelay || 30000,
      exponentialBackoff: options.exponentialBackoff !== false,
      jitter: options.jitter !== false,
      shouldRetry: options.shouldRetry || (() => true),
      onRetry: options.onRetry || (() => {}),
      onFail: options.onFail || (() => {}),
    };
  }

  async function withRetry(fn, options = {}) {
    const config = createRetryConfig(options);
    let lastError;

    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
      try {
        return await fn(attempt);
      } catch (error) {
        lastError = error;

        const shouldContinue =
          attempt < config.maxRetries && config.shouldRetry(error, attempt);

        if (shouldContinue) {
          const delay = config.exponentialBackoff
            ? calculateBackoff(attempt, config.retryDelay, config.maxDelay)
            : config.retryDelay;

          config.onRetry(error, attempt + 1, config.maxRetries, delay);
          await sleep(delay);
        } else {
          config.onFail(error, attempt);
          throw error;
        }
      }
    }

    throw lastError || new Error('Max retries exceeded');
  }

  async function retryUntilValid(fn, validator, options = {}) {
    const config = createRetryConfig(options);

    return withRetry(async (attempt) => {
      const result = await fn(attempt);

      if (!validator(result)) {
        throw new Error('Validation failed');
      }

      return result;
    }, config);
  }

  class CircuitBreaker {
    constructor(options = {}) {
      this.failureThreshold = options.failureThreshold || 5;
      this.resetTimeout = options.resetTimeout || 60000;
      this.state = 'CLOSED';
      this.failures = 0;
      this.nextAttempt = Date.now();
    }

    async call(fn) {
      if (this.state === 'OPEN') {
        if (Date.now() < this.nextAttempt) {
          throw new Error('Circuit breaker is OPEN');
        }
        this.state = 'HALF_OPEN';
      }

      try {
        const result = await fn();
        this.onSuccess();
        return result;
      } catch (error) {
        this.onFailure();
        throw error;
      }
    }

    onSuccess() {
      this.failures = 0;
      this.state = 'CLOSED';
    }

    onFailure() {
      this.failures++;
      if (this.failures >= this.failureThreshold) {
        this.state = 'OPEN';
        this.nextAttempt = Date.now() + this.resetTimeout;
      }
    }

    reset() {
      this.failures = 0;
      this.state = 'CLOSED';
      this.nextAttempt = Date.now();
    }
  }

  window.retryUtils = {
    calculateBackoff,
    sleep,
    createRetryConfig,
    withRetry,
    retryUntilValid,
    CircuitBreaker,
  };
})();
